import { Badge, Button, Card, Group, Modal, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { useState } from "react";
import { EndCustomerChargeApi } from "../../apis";
import type { EcCharge } from "../../apis/endCustomerCharge";
import { formatDate } from "../../utils/format";
import { MoneyCell } from "./MoneyCell";
import { PermissionGate } from "./PermissionGate";
import { SectionHeader } from "./SectionHeader";
import { ShortId } from "./ShortId";
import { TenantName } from "./TenantName";

interface Props {
  opened: boolean;
  onClose: () => void;
  charge: EcCharge | null;
  onMutated: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: "gray", INVOICED: "blue", PAID: "green",
  OVERDUE: "orange", FALLBACK_TRANSFERRED: "red", WAIVED: "gray"
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: "Хүлээгдэж буй", INVOICED: "Нэхэмжилсэн", PAID: "Төлсөн",
  OVERDUE: "Хугацаа хэтэрсэн", FALLBACK_TRANSFERRED: "Тенант рүү шилжүүлсэн", WAIVED: "Хүчингүй"
};

const MODE_LABEL: Record<string, string> = {
  VIA_TENANT: "Тенантаар", DIRECT_FROM_CUSTOMER: "Харилцагчаас шууд",
  DIRECT_WITH_TENANT_FALLBACK: "Шууд (нөөц: тенант)"
};

export function EcChargeDetailModal({ opened, onClose, charge, onMutated }: Props) {
  const [mode, setMode] = useState<"view" | "mark-paid" | "waive">("view");
  const [externalPaymentId, setExternalPaymentId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!charge) return null;

  const canMutate = charge.status === "PENDING" || charge.status === "OVERDUE" || charge.status === "INVOICED";

  const submitMarkPaid = async () => {
    setBusy(true); setErr(null);
    try {
      await EndCustomerChargeApi.markPaid(charge.id, {
        externalPaymentId: externalPaymentId || undefined
      });
      onMutated(); setMode("view"); setExternalPaymentId(""); onClose();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const submitWaive = async () => {
    if (!reason.trim()) { setErr("Шалтгаан оруулна уу"); return; }
    setBusy(true); setErr(null);
    try {
      await EndCustomerChargeApi.waive(charge.id, { reason: reason.trim() });
      onMutated(); setMode("view"); setReason(""); onClose();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={() => { onClose(); setMode("view"); setErr(null); }} title="EC шимтгэлийн дэлгэрэнгүй" centered size="lg">
      <Stack>
        <Group justify="space-between">
          <Text fw={600} size="lg" style={{ fontVariantNumeric: "tabular-nums" }}>{charge.period}</Text>
          <Badge color={STATUS_COLORS[charge.status]} variant="light" radius="sm">
            {STATUS_LABEL[charge.status] ?? charge.status}
          </Badge>
        </Group>
        <Card withBorder radius="md" padding="md">
          <Stack gap="xs">
            <SectionHeader eyebrow="EC шимтгэл" title="Дэлгэрэнгүй" />
            <Group justify="space-between"><Text c="dimmed" size="sm">Тенант</Text><TenantName id={charge.tenantId} /></Group>
            <Group justify="space-between"><Text c="dimmed" size="sm">Харилцагч</Text><ShortId id={charge.endCustomerBusinessId} /></Group>
            <Group justify="space-between"><Text c="dimmed" size="sm">Дүн</Text><MoneyCell value={charge.amount} currency={charge.currency} fw={600} /></Group>
            <Group justify="space-between"><Text c="dimmed" size="sm">Тооцооны горим</Text><Text size="sm">{MODE_LABEL[charge.collectionMode] ?? charge.collectionMode}</Text></Group>
            <Group justify="space-between"><Text c="dimmed" size="sm">Хугацаа дуусах</Text><Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{formatDate(charge.dueDate)}</Text></Group>
            {charge.paidAt && <Group justify="space-between"><Text c="dimmed" size="sm">Төлсөн</Text><Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{formatDate(charge.paidAt)}</Text></Group>}
          </Stack>
        </Card>

        {mode === "view" && canMutate && (
          <PermissionGate code="BIL_020">
            <Group>
              <Button variant="filled" color="green" onClick={() => setMode("mark-paid")}>Төлсөн гэж тэмдэглэх</Button>
              <Button variant="outline" color="red" onClick={() => setMode("waive")}>Хүчингүй болгох</Button>
            </Group>
          </PermissionGate>
        )}

        {mode === "mark-paid" && (
          <Stack>
            <TextInput
              label="External payment ID (заавал биш)"
              value={externalPaymentId}
              onChange={(e) => setExternalPaymentId(e.currentTarget.value)}
            />
            {err && <Text c="red" size="sm">{err}</Text>}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setMode("view")} disabled={busy}>Болих</Button>
              <Button onClick={submitMarkPaid} loading={busy} color="green">Төлсөн гэж тэмдэглэх</Button>
            </Group>
          </Stack>
        )}

        {mode === "waive" && (
          <Stack>
            <Textarea
              label="Шалтгаан" required minRows={3}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
            {err && <Text c="red" size="sm">{err}</Text>}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setMode("view")} disabled={busy}>Болих</Button>
              <Button onClick={submitWaive} loading={busy} color="red">Хүчингүй болгох</Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
